import React, { useState } from 'react';
import { 
  Book, 
  Trophy, 
  Target, 
  Calendar, 
  Gift, 
  Star, 
  Clock, 
  TrendingUp,
  Plus,
  ChevronRight,
  ExternalLink,
  CheckCircle,
  Circle
} from 'lucide-react';

export default function ActiveReadingView({ 
  challenge, 
  currentBook, 
  readingStats, 
  familyRecommendations,
  onUpdateProgress,
  onCompleteBook,
  onStartNewBook,
  onOpenBookSwitcher,
  onAddToAmazonList,
  isLoading = false 
}) {
  const [showMilestones, setShowMilestones] = useState(false);

  const getDaysRemaining = () => {
    if (!challenge?.end_date) return 0;
    const endDate = new Date(challenge.end_date);
    const today = new Date();
    const diffTime = endDate - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getProgressPercentage = () => {
    if (challenge?.challenge_type === 'book_count') {
      return Math.min(100, (challenge.current_books / challenge.target_books) * 100);
    } else {
      return Math.min(100, (challenge.current_pages / challenge.target_pages) * 100);
    }
  };

  const getMilestones = () => {
    const milestones = [];
    const target = challenge?.challenge_type === 'book_count' ? challenge.target_books : challenge.target_pages;
    const current = challenge?.challenge_type === 'book_count' ? challenge.current_books : challenge.current_pages;
    
    // Create milestone markers at 25%, 50%, 75%, 100%
    [0.25, 0.5, 0.75, 1.0].forEach((percent, index) => {
      const milestoneValue = Math.ceil(target * percent);
      const isCompleted = current >= milestoneValue;
      const reward = [2.50, 5.00, 7.50, challenge.reward_amount || 10.00][index];
      
      milestones.push({
        percentage: percent * 100,
        value: milestoneValue,
        reward,
        completed: isCompleted,
        label: `${percent * 100}% Complete`
      });
    });
    
    return milestones;
  };

  const getBookCover = (book) => {
    if (book?.cover_url) {
      return (
        <img 
          src={book.cover_url} 
          alt={book.book_title || book.title}
          className="w-full h-full object-cover rounded-lg"
        />
      );
    }
    
    const title = book?.book_title || book?.title || 'Unknown';
    const initials = title.split(' ').slice(0, 2).map(word => word.charAt(0)).join('').toUpperCase();
    
    return (
      <div className="w-full h-full bg-gradient-to-br from-azure-500 to-violet-600 rounded-lg flex items-center justify-center text-white font-bold text-xl">
        {initials}
      </div>
    );
  };

  const renderBookGrid = (books, title, emptyMessage) => (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-violet-200">
      <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
        <Book className="w-5 h-5 text-violet-600" />
        {title}
      </h3>
      
      {books && books.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {books.slice(0, 8).map((book, index) => (
            <div key={book.id || index} className="group cursor-pointer">
              <div className="aspect-[3/4] rounded-lg overflow-hidden shadow-md mb-3 relative">
                {getBookCover(book)}
                
                {/* Hover overlay with actions */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-2">
                    <button 
                      onClick={() => onStartNewBook(book)}
                      className="p-2 bg-white rounded-full shadow-lg hover:bg-stone-50"
                      title="Start reading"
                    >
                      <Book className="w-4 h-4 text-violet-600" />
                    </button>
                    {onAddToAmazonList && (
                      <button 
                        onClick={() => onAddToAmazonList(book)}
                        className="p-2 bg-white rounded-full shadow-lg hover:bg-stone-50"
                        title="View on Amazon"
                      >
                        <ExternalLink className="w-4 h-4 text-orange-600" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <h4 className="text-sm font-medium text-slate-900 truncate" title={book.book_title || book.title}>
                  {book.book_title || book.title}
                </h4>
                <p className="text-xs text-slate-600 truncate" title={book.book_author || book.author}>
                  {book.book_author || book.author}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-stone-500">
          <Book className="w-12 h-12 mx-auto mb-4 text-stone-300" />
          <p>{emptyMessage}</p>
        </div>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 to-azure-50 flex items-center justify-center">
        <div className="text-center">
          <Trophy className="w-12 h-12 text-violet-500 animate-pulse mx-auto mb-4" />
          <p className="text-stone-600">Loading your challenge...</p>
        </div>
      </div>
    );
  }

  const progressPercentage = getProgressPercentage();
  const daysRemaining = getDaysRemaining();
  const milestones = getMilestones();

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-azure-50 p-4">
      <div className="max-w-6xl mx-auto space-y-6">
        
        {/* Challenge Header */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-violet-200">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-azure-600 rounded-xl flex items-center justify-center">
                <Trophy className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{challenge?.challenge_name}</h1>
                <p className="text-slate-600">{challenge?.challenge_description}</p>
              </div>
            </div>
            
            <div className="text-right">
              <div className="text-3xl font-bold text-violet-600">{Math.round(progressPercentage)}%</div>
              <div className="text-sm text-slate-600">Complete</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-slate-600 mb-2">
              <span>Progress</span>
              <span>{daysRemaining} days remaining</span>
            </div>
            <div className="relative w-full bg-stone-200 rounded-full h-4">
              <div 
                className="bg-gradient-to-r from-violet-500 to-azure-600 h-4 rounded-full transition-all duration-500 relative"
                style={{ width: `${progressPercentage}%` }}
              >
                {/* Milestone markers */}
                {milestones.map((milestone, index) => (
                  <div
                    key={index}
                    className={`absolute top-0 w-1 h-4 ${
                      milestone.completed ? 'bg-yellow-400' : 'bg-white opacity-50'
                    }`}
                    style={{ left: `${milestone.percentage}%` }}
                    title={`${milestone.label}: $${milestone.reward}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-3 md:grid-cols-5 gap-4 text-center">
            <div className="bg-violet-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-violet-600">
                {challenge?.challenge_type === 'book_count' ? challenge?.current_books : challenge?.current_pages}
              </div>
              <div className="text-xs text-slate-600">
                of {challenge?.challenge_type === 'book_count' ? challenge?.target_books : challenge?.target_pages}
              </div>
            </div>
            
            <div className="bg-azure-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-azure-600">{daysRemaining}</div>
              <div className="text-xs text-slate-600">Days Left</div>
            </div>
            
            <div className="bg-emerald-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-emerald-600">${challenge?.reward_amount?.toFixed(2) || '0.00'}</div>
              <div className="text-xs text-slate-600">Total Reward</div>
            </div>
            
            <div className="bg-orange-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-orange-600">{readingStats?.currentStreak || 0}</div>
              <div className="text-xs text-slate-600">Day Streak</div>
            </div>
            
            <div className="bg-pink-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-pink-600">${readingStats?.totalEarned?.toFixed(2) || '0.00'}</div>
              <div className="text-xs text-slate-600">Earned</div>
            </div>
          </div>
        </div>

        {/* Current Book */}
        {currentBook && (
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-violet-200">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Book className="w-5 h-5 text-violet-600" />
              Currently Reading
            </h2>
            
            <div className="flex gap-6">
              <div className="w-24 h-32 flex-shrink-0">
                {getBookCover(currentBook)}
              </div>
              
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-900 mb-1">{currentBook.book_title}</h3>
                <p className="text-violet-600 mb-3">by {currentBook.book_author}</p>
                
                <div className="mb-4">
                  <div className="flex justify-between text-sm text-slate-600 mb-1">
                    <span>Page {currentBook.current_page}</span>
                    <span>of {currentBook.total_pages}</span>
                  </div>
                  <div className="w-full bg-stone-200 rounded-full h-2">
                    <div 
                      className="bg-violet-500 h-2 rounded-full"
                      style={{ width: `${(currentBook.current_page / currentBook.total_pages) * 100}%` }}
                    />
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={onOpenBookSwitcher}
                    className="px-4 py-2 border border-violet-300 text-violet-600 rounded-lg hover:bg-violet-50 transition-colors"
                  >
                    Switch Book
                  </button>
                  
                  <button
                    onClick={() => onCompleteBook(currentBook.id)}
                    className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
                  >
                    Mark Complete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Milestone Progress */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-violet-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Target className="w-5 h-5 text-violet-600" />
              Milestone Rewards
            </h2>
            <button
              onClick={() => setShowMilestones(!showMilestones)}
              className="text-violet-600 hover:text-violet-700"
            >
              <ChevronRight className={`w-5 h-5 transition-transform ${showMilestones ? 'rotate-90' : ''}`} />
            </button>
          </div>

          {showMilestones && (
            <div className="space-y-3">
              {milestones.map((milestone, index) => (
                <div key={index} className={`flex items-center gap-4 p-3 rounded-lg ${
                  milestone.completed ? 'bg-emerald-50 border border-emerald-200' : 'bg-stone-50 border border-stone-200'
                }`}>
                  {milestone.completed ? (
                    <CheckCircle className="w-6 h-6 text-emerald-500" />
                  ) : (
                    <Circle className="w-6 h-6 text-stone-400" />
                  )}
                  
                  <div className="flex-1">
                    <div className="font-medium text-slate-900">{milestone.label}</div>
                    <div className="text-sm text-slate-600">
                      Reach {milestone.value} {challenge?.challenge_type === 'book_count' ? 'books' : 'pages'}
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="font-bold text-violet-600">${milestone.reward.toFixed(2)}</div>
                    {milestone.completed && (
                      <div className="text-xs text-emerald-600">✓ Earned</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Book Recommendations */}
        {familyRecommendations && renderBookGrid(
          familyRecommendations, 
          "Family Recommendations", 
          "No recommendations available"
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={onOpenBookSwitcher}
            className="bg-white rounded-xl p-4 shadow-sm border border-violet-200 hover:bg-violet-50 transition-colors text-center"
          >
            <Book className="w-8 h-8 text-violet-600 mx-auto mb-2" />
            <div className="font-medium text-slate-900">Switch Book</div>
          </button>
          
          <button className="bg-white rounded-xl p-4 shadow-sm border border-violet-200 hover:bg-azure-50 transition-colors text-center">
            <TrendingUp className="w-8 h-8 text-azure-600 mx-auto mb-2" />
            <div className="font-medium text-slate-900">View Progress</div>
          </button>
          
          <button className="bg-white rounded-xl p-4 shadow-sm border border-violet-200 hover:bg-emerald-50 transition-colors text-center">
            <Gift className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
            <div className="font-medium text-slate-900">My Rewards</div>
          </button>
          
          <button className="bg-white rounded-xl p-4 shadow-sm border border-violet-200 hover:bg-orange-50 transition-colors text-center">
            <Plus className="w-8 h-8 text-orange-600 mx-auto mb-2" />
            <div className="font-medium text-slate-900">Add Books</div>
          </button>
        </div>
      </div>
    </div>
  );
}